import React, { useEffect, useState } from "react";
import axios from "axios";
import { Search } from "lucide-react";

import API from "../../../services/api";


interface Product {
  _id: string;
  name: string;
  image?: string;
  description?: string;
  kr_price?: string | number;
}

interface Category {
  _id: string;
  name: string;
}

const PRODUCTS_PER_PAGE = 20;

export default function Products() {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] =
    useState<Product[]>([]);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [pujaCategoryId, setPujaCategoryId] =
    useState<string>("");

  /* =========================================================
     FIND PUJA ITEMS CATEGORY
  ========================================================= */

  const loadPujaCategory = async () => {
    try {
      const response = await API.get("/categories");

      const categories: Category[] =
        response.data?.data?.categories || [];

      const pujaCategory = categories.find(
        (category) =>
          category.name?.trim().toLowerCase() ===
          "puja items"
      );

      if (!pujaCategory) {
        console.error("Puja Items category not found");

        setProducts([]);
        setFilteredProducts([]);

        return;
      }

      setPujaCategoryId(pujaCategory._id);

      await fetchProducts(
        1,
        pujaCategory._id
      );
    } catch (error) {
      console.error(
        "CATEGORY ERROR:",
        error
      );

      setProducts([]);
      setFilteredProducts([]);
    }
  };

  /* =========================================================
     FETCH ONLY PUJA ITEMS
  ========================================================= */

  const fetchProducts = async (
    page: number,
    categoryId: string
  ) => {
    try {
      setLoading(true);

      const response = await API.get(
        "/products",
        {
          params: {
            page,
            limit: PRODUCTS_PER_PAGE,
            category_id: categoryId,
          },
        }
      );

      console.log(
        "PUJA PRODUCTS:",
        response.data
      );

      const responseData =
        response.data?.data;

      let productArray: Product[] = [];

      if (Array.isArray(responseData)) {
        productArray = responseData;
      } else if (
        Array.isArray(
          responseData?.products
        )
      ) {
        productArray =
          responseData.products;
      }

      const normalizedProducts =
        productArray.map(
          (product) => ({
            ...product,

            kr_price:
              product.kr_price !== undefined &&
              product.kr_price !== null
                ? String(product.kr_price)
                : "",
          })
        );

      setProducts(
        normalizedProducts
      );

      setFilteredProducts(
        normalizedProducts
      );

      setCurrentPage(page);

      setHasNextPage(
        productArray.length ===
          PRODUCTS_PER_PAGE
      );
    } catch (error) {
      console.error(
        "PRODUCT ERROR:",
        error
      );

      if (
        axios.isAxiosError(error)
      ) {
        console.log(
          error.response?.data
        );
      }

      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    loadPujaCategory();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================
     SEARCH
  ========================================================= */

  useEffect(() => {
    const searchText =
      search.trim().toLowerCase();

    if (!searchText) {
      setFilteredProducts(
        products
      );

      return;
    }

    const filtered =
      products.filter(
        (product) => {
          const name =
            product.name
              ?.toLowerCase() || "";

          const description =
            product.description
              ?.toLowerCase() || "";

          return (
            name.includes(searchText) ||
            description.includes(searchText)
          );
        }
      );

    setFilteredProducts(
      filtered
    );
  }, [search, products]);

  /* =========================================================
     PREVIOUS PAGE
  ========================================================= */

  const handlePreviousPage = () => {
    if (
      currentPage <= 1 ||
      loading ||
      !pujaCategoryId
    ) {
      return;
    }

    setSearch("");

    fetchProducts(
      currentPage - 1,
      pujaCategoryId
    );
  };

  /* =========================================================
     NEXT PAGE
  ========================================================= */

  const handleNextPage = () => {
    if (
      !hasNextPage ||
      loading ||
      !pujaCategoryId
    ) {
      return;
    }

    setSearch("");

    fetchProducts(
      currentPage + 1,
      pujaCategoryId
    );
  };

  return (
    <>
      <div className="min-h-screen bg-purple-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">

          {/* TITLE */}

          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Puja Items - KR Market Price
          </h2>

          {/* SEARCH */}

          <div className="relative mb-6">
            <Search
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              value={search}
              placeholder="Search product..."
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
              className="
                w-full
                bg-white
                border
                border-gray-200
                rounded-xl
                pl-12
                pr-4
                py-3
                text-sm
                outline-none
                shadow-sm
                focus:ring-2
                focus:ring-purple-500
                focus:border-purple-500
              "
            />
          </div>

          {/* PRODUCTS */}

          {loading ? (
            <div className="bg-white border border-gray-100 rounded-xl p-10 text-center text-gray-500">
              Loading puja items...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl p-10 text-center text-gray-500">
              No puja items found
            </div>
          ) : (
            <>
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">

                {/* HEADER */}

                <div
                  className="
                    hidden
                    md:grid
                    md:grid-cols-[80px_1fr_160px]
                    items-center
                    gap-4
                    px-5
                    py-3
                    bg-gray-50
                    border-b
                    border-gray-200
                    text-sm
                    font-semibold
                    text-gray-500
                  "
                >
                  <div>
                    Image
                  </div>

                  <div>
                    Product
                  </div>

                  <div className="text-right">
                    KR Market Price
                  </div>
                </div>

                {/* LIST */}

                {filteredProducts.map(
                  (product) => (
                    <div
                      key={product._id}
                      className="
                        flex
                        md:grid
                        md:grid-cols-[80px_1fr_160px]
                        items-center
                        gap-4
                        px-4
                        md:px-5
                        py-4
                        border-b
                        border-gray-100
                        last:border-b-0
                        hover:bg-purple-50/50
                        transition
                      "
                    >
                      {/* IMAGE */}

                      <img
                        src={
                          product.image ||
                          "/placeholder.png"
                        }
                        alt={product.name}
                        className="
                          w-16
                          h-16
                          rounded-xl
                          object-cover
                          flex-shrink-0
                          border
                          border-gray-100
                        "
                      />

                      {/* PRODUCT */}

                      <div className="flex-1 min-w-0">

                        <p
                          className="
                            text-base
                            md:text-lg
                            font-semibold
                            text-gray-900
                          "
                        >
                          {product.name}
                        </p>

                        {product.description && (
                          <p
                            className="
                              mt-1
                              text-sm
                              font-bold
                              text-gray-700
                              leading-snug
                            "
                          >
                            {product.description}
                          </p>
                        )}

                      </div>

                      {/* KR PRICE */}

                      <div className="ml-auto text-right flex-shrink-0">

                        <p className="md:hidden text-xs text-gray-400 mb-1">
                          KR Market Price
                        </p>

                        <p
                          className="
                            text-lg
                            md:text-xl
                            font-bold
                            text-purple-600
                          "
                        >
                          {product.kr_price !== "" &&
                          product.kr_price !== undefined
                            ? `₹${product.kr_price}`
                            : "—"}
                        </p>

                      </div>
                    </div>
                  )
                )}

              </div>

              {/* PAGINATION */}

              <div
                className="
                  flex
                  items-center
                  justify-center
                  gap-5
                  mt-8
                "
              >
                <button
                  type="button"
                  disabled={
                    currentPage === 1 ||
                    loading
                  }
                  onClick={
                    handlePreviousPage
                  }
                  className="
                    px-5
                    py-2.5
                    rounded-lg
                    border
                    border-gray-300
                    bg-white
                    text-gray-700
                    font-semibold
                    hover:bg-gray-50
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                  "
                >
                  ← Back
                </button>

                <div
                  className="
                    px-4
                    py-2
                    rounded-lg
                    bg-purple-100
                    text-purple-700
                    font-bold
                  "
                >
                  Page {currentPage}
                </div>

                <button
                  type="button"
                  disabled={
                    !hasNextPage ||
                    loading
                  }
                  onClick={
                    handleNextPage
                  }
                  className="
                    px-5
                    py-2.5
                    rounded-lg
                    bg-purple-600
                    text-white
                    font-semibold
                    hover:bg-purple-700
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                  "
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
